import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
  Dialog,
  DialogActions,
  DialogContent,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveIcon from '@mui/icons-material/Remove';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import NightsStayOutlinedIcon from '@mui/icons-material/NightsStayOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import BedOutlinedIcon from '@mui/icons-material/BedOutlined';
import KingBedOutlinedIcon from '@mui/icons-material/KingBedOutlined';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import dayjs from 'dayjs';
import axiosInstance from 'utils/axios';
import { URLS } from 'utils/constants/urls';
import PageLoader from 'components/PageLoader';
import Notification from 'components/Notification';
import strings from 'common/Translation/Translate';
import DateInput from 'components/DateInput/DateInput';
import { identifiers } from 'utils/constants/identifiers';

export type YcsRoomSummary = {
  hotelId?: string;
  roomId?: string;
  /** YCS Room_Name */
  roomName?: string;
  /** YCS Roomtype_Name — grouping / accordion title */
  roomtypeName?: string;
  packageName?: string;
  description?: string;
  /** YCS unique IDs needed when the booking has to be pushed back to YCS via mediator. */
  roomTypeUnkId?: string;
  rateTypeUnkId?: string;
  ratePlanUnkId?: string;
  packageId?: string;
  occupancy?: {
    baseAdults?: number;
    baseChildren?: number;
    maxAdults?: number;
    maxChildren?: number;
  };
  availability?: {
    date?: string;
    availableRooms?: number;
    isSoldOut?: boolean;
    minNights?: number;
    stopSell?: boolean;
  };
  pricing?: {
    currency?: string;
    /** Tax-exclusive base room rate per night. */
    pricePerNight?: number;
    /** Tax-inclusive room rate per night (reference). */
    priceInclTax?: number;
    /** Effective tax percent on the base room rate. */
    taxPercent?: number;
    originalPrice?: number;
    discountPercent?: number;
    taxIncluded?: boolean;
  };
  extraPricing?: {
    /** Tax-exclusive extra-adult rate per night. */
    extraAdult?: number;
    /** Tax-exclusive extra-child rate per night. */
    extraChild?: number;
    /** Effective tax percent on the extra-adult rate. */
    extraAdultTaxPercent?: number;
    /** Effective tax percent on the extra-child rate. */
    extraChildTaxPercent?: number;
  };
  amenities?: string[];
  checkIn?: string;
  checkOut?: string;
  bookingUrl?: string;
};

export type YcsSelectedRoomLine = {
  lineId: string;
  roomId?: string;
  roomName?: string;
  packageName?: string;
  quantity: number;
  adultsPerRoom: number;
  childrenPerRoom: number;
  /** Per room per night — defaults from YCS search but can be overridden before save. */
  pricePerNight: number;
  summary: YcsRoomSummary;
};

/**
 * Per-business-date line item that matches the backend
 * {@code AdminBookedRoomLineItemRequest} shape.
 */
export type ExternalBookedRoomLineItemPayload = {
  date: string;
  provider: string;
  quantity: number;
  adults: number;
  children: number;
  baseAdultOccupancy?: number;
  baseChildOccupancy?: number;
  roomTypeId?: string;
  roomTypeName?: string;
  mealPlanId?: string;
  mealPlanName?: string;
  /** YCS Rateplan_Id (roomrateunkid) — required when the booking must be pushed to YCS. */
  ratePlanId?: string;
  /** YCS Ratetype_Id (ratetypeunkid). */
  rateTypeId?: string;
  packageId?: string;
  packageName?: string;
  baseRoomPrice?: number;
  mealPlanPrice?: number;
  adultPrice?: number;
  childPrice?: number;
  extraAdultPrice?: number;
  extraChildPrice?: number;
  /** Effective tax percent per rate component, forwarded so the backend can store tax. */
  baseTaxPercent?: number;
  extraAdultTaxPercent?: number;
  extraChildTaxPercent?: number;
  totalTaxAmount?: number;
};

/**
 * One external bookedRoom (room-type × rate-plan) with per-date line items — matches the
 * backend {@code AdminExternalRoomBookingRequest} shape. The parent screen forwards these
 * straight through to the {@code /new-booking} or {@code /booking-items/{id}} payloads.
 */
export type ExternalBookedRoomPayload = {
  provider: string;
  currency?: string;
  serviceChargePercentage?: number;
  lineItems: ExternalBookedRoomLineItemPayload[];
};

type RoomGroup = {
  key: string;
  displayName: string;
  offers: YcsRoomSummary[];
};

/**
 * Rendered at the top of the YCS tab when an edit-booking already has rooms persisted against
 * it. Mirrors the {@code BookedRoomResponse} shape coming back from
 * {@code GET /authenticated/booking-items/{id}/master-data} — every field is optional because
 * the legacy in-house flow only populates a subset.
 */
export type ExistingBookedRoomSummary = {
  bookedRoomId: number;
  externalProvider?: string;
  externalReservationNo?: string;
  externalSubReservationNos?: string;
  type?: string;
  roomTypeCode?: string;
  packageName?: string;
  ratePlanId?: string;
  rateTypeId?: string;
  from?: string;
  to?: string;
  numberOfRooms?: number;
  adultsPerRoom?: number;
  childrenPerRoom?: number;
  baseRoomPrice?: number;
  pricePerDay?: number;
  mealPlanName?: string;
  mealPlanPrice?: number;
  extraAdultPrice?: number;
  extraChildPrice?: number;
  lineItems?: Array<{
    id?: number;
    businessDate?: string;
    quantity?: number;
    adults?: number;
    children?: number;
    baseRoomPrice?: number;
    extraAdultPrice?: number;
    extraChildPrice?: number;
    baseTaxPercent?: number;
    totalBaseAmount?: number;
    totalTaxAmount?: number;
    totalAmount?: number;
  }>;
};

/** Rooms that share one channel-manager reservation (e.g. one YCS ReservationNo). */
export type ExistingReservationGroup = {
  reservationKey: string;
  externalProvider?: string;
  externalReservationNo?: string;
  rooms: ExistingBookedRoomSummary[];
  isSharedYcsReservation: boolean;
};

function groupExistingBookedRooms(rooms: ExistingBookedRoomSummary[]): ExistingReservationGroup[] {
  const byReservationNo = new Map<string, ExistingBookedRoomSummary[]>();
  const standalone: ExistingBookedRoomSummary[] = [];

  rooms.forEach(room => {
    if (room.externalProvider && room.externalReservationNo) {
      const key = room.externalReservationNo;
      if (!byReservationNo.has(key)) {
        byReservationNo.set(key, []);
      }
      byReservationNo.get(key)!.push(room);
    } else {
      standalone.push(room);
    }
  });

  const groups: ExistingReservationGroup[] = [];
  byReservationNo.forEach((groupRooms, reservationNo) => {
    groups.push({
      reservationKey: `ycs-${reservationNo}`,
      externalProvider: groupRooms[0]?.externalProvider,
      externalReservationNo: reservationNo,
      rooms: groupRooms,
      isSharedYcsReservation: true,
    });
  });
  standalone.forEach(room => {
    groups.push({
      reservationKey: `room-${room.bookedRoomId}`,
      rooms: [room],
      isSharedYcsReservation: false,
    });
  });
  return groups;
}

type ExistingRoomTotals = { subtotal: number; tax: number; total: number; baseTaxPercent?: number };

function existingRoomNights(room: ExistingBookedRoomSummary): number {
  if (room.from && room.to) {
    return Math.max(0, dayjs(room.to, 'DD-MM-YYYY').diff(dayjs(room.from, 'DD-MM-YYYY'), 'day'));
  }
  return room.lineItems?.length || 0;
}

/** Subtotal, tax, and total for a persisted room — prefers stored line-item amounts. */
function computeExistingRoomTotals(room: ExistingBookedRoomSummary): ExistingRoomTotals {
  if (room.lineItems?.length) {
    const subtotal = room.lineItems.reduce((s, li) => s + num(li.totalBaseAmount), 0);
    const tax = room.lineItems.reduce((s, li) => s + num(li.totalTaxAmount), 0);
    const total = room.lineItems.reduce((s, li) => s + num(li.totalAmount), 0);
    if (subtotal > 0 || tax > 0 || total > 0) {
      const baseTaxPercent = room.lineItems.find(li => num(li.baseTaxPercent) > 0)?.baseTaxPercent;
      return { subtotal, tax, total: total || subtotal + tax, baseTaxPercent };
    }
  }

  const nights = existingRoomNights(room);
  const rate = room.baseRoomPrice ?? room.pricePerDay ?? 0;
  const qty = room.numberOfRooms || room.lineItems?.[0]?.quantity || 1;
  const subtotal = nights * qty * rate;
  return { subtotal, tax: 0, total: subtotal };
}

function computeExistingGroupTotals(rooms: ExistingBookedRoomSummary[]): ExistingRoomTotals {
  return rooms.reduce(
    (acc, room) => {
      const t = computeExistingRoomTotals(room);
      return {
        subtotal: acc.subtotal + t.subtotal,
        tax: acc.tax + t.tax,
        total: acc.total + t.total,
        baseTaxPercent: acc.baseTaxPercent ?? t.baseTaxPercent,
      };
    },
    { subtotal: 0, tax: 0, total: 0 } as ExistingRoomTotals,
  );
}

export type YcsRoomBookingProps = {
  /** When set, initializes / syncs search dates (e.g. event start/end from New Booking). */
  eventStartDate?: string | Date | null;
  eventEndDate?: string | Date | null;
  /** Called whenever the user adds or removes selected room lines. */
  onSelectionChange?: (lines: YcsSelectedRoomLine[]) => void;
  /**
   * Called whenever the selection or search dates change. Emits the payload ready to be
   * sent to the backend under {@code externalBookedRooms}.
   */
  onPayloadChange?: (payload: ExternalBookedRoomPayload[]) => void;
  /**
   * Rooms already persisted against the booking. Rendered in a dedicated panel so the user
   * can see the live reservation numbers and cancel them individually.
   */
  existingBookedRooms?: ExistingBookedRoomSummary[];
  /**
   * Invoked when the user cancels an existing reservation group. For YCS, pass any room from
   * the group — the backend cancels the shared ReservationNo and removes all sibling rows.
   */
  onCancelExistingBookedRoom?: (group: ExistingReservationGroup) => void;
};

function groupKey(r: YcsRoomSummary, fallbackIdx: number): string {
  if (r.roomId != null && String(r.roomId).length > 0) return String(r.roomId);
  return `name:${(r.roomtypeName || r.roomName || 'room').replace(/\s+/g, '_')}:${fallbackIdx}`;
}

function buildGroups(rows: YcsRoomSummary[]): RoomGroup[] {
  const map = new Map<string, YcsRoomSummary[]>();
  rows.forEach((r, idx) => {
    const k = groupKey(r, idx);
    if (!map.has(k)) map.set(k, []);
    map.get(k)!.push(r);
  });
  return Array.from(map.entries()).map(([key, offers]) => {
    const sorted = [...offers].sort((a, b) => {
      const pa: any = a.pricing?.pricePerNight ?? 0;
      const pb = b.pricing?.pricePerNight ?? 0;
      return pa - pb;
    });
    const first = sorted[0];
    return {
      key,
      displayName: first.roomtypeName || first.roomName || first.packageName || 'Room',
      offers: sorted,
    };
  });
}

function num(v: unknown): number {
  if (v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Extra charge per room per night above base occupancy (matches YCS extra adult/child fields). */
function perRoomNightExtra(summary: YcsRoomSummary, adults: number, children: number): number {
  const baseA = summary.occupancy?.baseAdults ?? 0;
  const baseC = summary.occupancy?.baseChildren ?? 0;
  const ea = num(summary.extraPricing?.extraAdult);
  const ec = num(summary.extraPricing?.extraChild);
  const extraA = Math.max(0, adults - baseA);
  const extraC = Math.max(0, children - baseC);
  return extraA * ea + extraC * ec;
}

/**
 * Tax per room per night using the effective percent for each rate component. The base price is
 * tax-exclusive and may be overridden by the user — the same base percent is re-applied so the
 * override carries tax too. Extra adult/child charges use their own percents.
 */
function perRoomNightTax(summary: YcsRoomSummary, base: number, adults: number, children: number): number {
  const baseA = summary.occupancy?.baseAdults ?? 0;
  const baseC = summary.occupancy?.baseChildren ?? 0;
  const extraA = Math.max(0, adults - baseA);
  const extraC = Math.max(0, children - baseC);
  const basePct = num(summary.pricing?.taxPercent);
  const eaPct = num(summary.extraPricing?.extraAdultTaxPercent);
  const ecPct = num(summary.extraPricing?.extraChildTaxPercent);
  const ea = num(summary.extraPricing?.extraAdult);
  const ec = num(summary.extraPricing?.extraChild);
  return (base * basePct) / 100 + (extraA * ea * eaPct) / 100 + (extraC * ec * ecPct) / 100;
}

function formatMoney(currency: string | undefined, amount: number): string {
  const cur = currency?.trim() || '';
  if (!cur) return amount.toFixed(2);
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur, maximumFractionDigits: 2 }).format(
      amount,
    );
  } catch {
    return `${cur} ${amount.toFixed(2)}`;
  }
}

function nightsBetween(checkIn: string, checkOut: string): number {
  const n = dayjs(checkOut).diff(dayjs(checkIn), 'day');
  return Math.max(0, n);
}

type DraftState = {
  offerIndex: number;
  quantity: number;
  adultsPerRoom: number;
  childrenPerRoom: number;
  pricePerNight: number;
};

const defaultDraftForOffer = (offer: YcsRoomSummary, offerIndex = 0): DraftState => ({
  offerIndex,
  quantity: 1,
  adultsPerRoom: Math.max(1, offer.occupancy?.baseAdults ?? 2),
  childrenPerRoom: Math.max(0, offer.occupancy?.baseChildren ?? 0),
  pricePerNight: num(offer.pricing?.pricePerNight),
});

function linePricePerNight(line: YcsSelectedRoomLine): number {
  const override = num(line.pricePerNight);
  if (override > 0) return override;
  return num(line.summary.pricing?.pricePerNight);
}

const compactGuestFieldSx = {
  width: '100%',
  minWidth: 0,
  '& .MuiInputLabel-root': { fontSize: '0.7rem' },
  '& .MuiOutlinedInput-root': { pl: 0.25, pr: 0.25 },
  '& .MuiOutlinedInput-input': { px: 0.5, py: 0.75, fontSize: '0.8125rem' },
  '& .MuiInputAdornment-root': { mx: 0 },
  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
};

/** Price is typed manually — no spinners and no scroll-to-increment. */
const compactPriceFieldSx = {
  ...compactGuestFieldSx,
  '& input': { MozAppearance: 'textfield' },
};

const blockScrollWheelOnInput = (e: React.WheelEvent<HTMLInputElement>) => {
  e.preventDefault();
};

const MAX_PRICE_DECIMALS = 2;
const priceInputPattern = /^\d*(\.\d{0,2})?$/;

function parsePriceInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '' || trimmed === '.') return 0;
  if (!priceInputPattern.test(trimmed)) return null;
  const v = parseFloat(trimmed);
  if (!Number.isFinite(v)) return null;
  const factor = 10 ** MAX_PRICE_DECIMALS;
  return Math.max(0, Math.round(v * factor) / factor);
}

/** A compact +/- stepper rendered inside a TextField's adornments. */
const Stepper: FC<{
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (n: number) => void;
}> = ({ label, value, min, max, onChange }) => {
  const clamp = (n: number) => Math.min(max, Math.max(min, n));
  return (
    <TextField
      size="small"
      label={label}
      type="number"
      value={value}
      onChange={e => onChange(clamp(parseInt(e.target.value, 10) || min))}
      inputProps={{ min, max, style: { textAlign: 'center', MozAppearance: 'textfield' } }}
      sx={compactGuestFieldSx}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start" sx={{ ml: 0.25 }}>
            <IconButton
              size="small"
              sx={{
                padding: 0.2,
                bgcolor: 'primary.main',
                color: 'common.white',
                '&:hover': { bgcolor: 'primary.dark' },
                '&:disabled': {
                  bgcolor: 'action.disabledBackground',
                  color: 'text.disabled',
                },
              }}
              aria-label={`Decrease ${label}`}
              disabled={value <= min}
              onClick={() => onChange(clamp(value - 1))}>
              <RemoveIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end" sx={{ mr: 0.25 }}>
            <IconButton
              size="small"
              sx={{
                padding: 0.2,
                bgcolor: 'primary.main',
                color: 'common.white',
                '&:hover': { bgcolor: 'primary.dark' },
                '&:disabled': {
                  bgcolor: 'action.disabledBackground',
                  color: 'text.disabled',
                },
              }}
              aria-label={`Increase ${label}`}
              disabled={value >= max}
              onClick={() => onChange(clamp(value + 1))}>
              <AddIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};

/** Builds the backend payload (one entry per selected line, expanded into per-night line items). */
function buildExternalPayload(
  selections: YcsSelectedRoomLine[],
  checkIn: string,
  checkOut: string,
): ExternalBookedRoomPayload[] {
  if (!selections.length) return [];
  const startD = dayjs(checkIn);
  const endD = dayjs(checkOut);
  if (!startD.isValid() || !endD.isValid() || !endD.isAfter(startD)) return [];

  const dates: string[] = [];
  for (let d = startD; d.isBefore(endD); d = d.add(1, 'day')) {
    dates.push(d.format('YYYY-MM-DD'));
  }

  return selections.map(line => {
    const s = line.summary;
    const baseRoomPrice = linePricePerNight(line);
    const extraAdultPrice = num(s.extraPricing?.extraAdult);
    const extraChildPrice = num(s.extraPricing?.extraChild);
    const baseAdults = s.occupancy?.baseAdults ?? 0;
    const baseChildren = s.occupancy?.baseChildren ?? 0;
    const baseTaxPercent = num(s.pricing?.taxPercent);
    const extraAdultTaxPercent = num(s.extraPricing?.extraAdultTaxPercent);
    const extraChildTaxPercent = num(s.extraPricing?.extraChildTaxPercent);
    // Tax for a single room-night (extras depend on guests above base occupancy). The backend
    // multiplies by quantity, so keep this per-room-per-night.
    const taxPerRoomNight = perRoomNightTax(s, baseRoomPrice, line.adultsPerRoom, line.childrenPerRoom);

    const lineItems: ExternalBookedRoomLineItemPayload[] = dates.map(date => ({
      date,
      provider: 'YCS',
      quantity: line.quantity,
      adults: line.adultsPerRoom,
      children: line.childrenPerRoom,
      baseAdultOccupancy: baseAdults,
      baseChildOccupancy: baseChildren,
      roomTypeId: s.roomTypeUnkId || s.roomId,
      roomTypeName: s.roomtypeName || s.roomName || line.roomName,
      mealPlanName: s.packageName || line.packageName,
      ratePlanId: s.ratePlanUnkId,
      rateTypeId: s.rateTypeUnkId,
      packageId: s.packageId,
      packageName: s.packageName,
      baseRoomPrice,
      extraAdultPrice,
      extraChildPrice,
      baseTaxPercent,
      extraAdultTaxPercent,
      extraChildTaxPercent,
      totalTaxAmount: taxPerRoomNight * line.quantity,
    }));

    return {
      provider: 'YCS',
      currency: s.pricing?.currency,
      lineItems,
    };
  });
}

export const YcsRoomBooking: FC<YcsRoomBookingProps> = ({
  eventStartDate,
  eventEndDate,
  onSelectionChange,
  onPayloadChange,
  existingBookedRooms,
  onCancelExistingBookedRoom,
}) => {
  const theme = useTheme();

  const fromEvent = useMemo(() => {
    const s = eventStartDate != null && eventStartDate !== '' ? dayjs(eventStartDate) : null;
    const e = eventEndDate != null && eventEndDate !== '' ? dayjs(eventEndDate) : null;
    if (s?.isValid() && e?.isValid() && e.isAfter(s)) {
      return { checkIn: s.format('YYYY-MM-DD'), checkOut: e.format('YYYY-MM-DD') };
    }
    return null;
  }, [eventStartDate, eventEndDate]);

  const [checkInDate, setCheckInDate] = useState<string>(
    fromEvent?.checkIn ?? dayjs().add(1, 'day').format('YYYY-MM-DD'),
  );
  const [checkOutDate, setCheckOutDate] = useState<string>(
    fromEvent?.checkOut ?? dayjs().add(2, 'day').format('YYYY-MM-DD'),
  );
  const [loading, setLoading] = useState(false);
  const [fetchedRooms, setFetchedRooms] = useState<YcsRoomSummary[]>([]);
  const [selections, setSelections] = useState<YcsSelectedRoomLine[]>([]);
  const [draftByGroup, setDraftByGroup] = useState<Record<string, DraftState>>({});
  /** Collapsed state keyed by group.key — only first group auto-expands to keep the list compact. */
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [message, setMessage] = useState<{ display: boolean; severity: any; message: string }>({
    display: false,
    severity: null,
    message: '',
  });
  const [cancelCandidate, setCancelCandidate] = useState<ExistingReservationGroup | null>(null);

  const existingReservationGroups = useMemo(
    () => (existingBookedRooms?.length ? groupExistingBookedRooms(existingBookedRooms) : []),
    [existingBookedRooms],
  );

  useEffect(() => {
    if (fromEvent) {
      setCheckInDate(fromEvent.checkIn);
      setCheckOutDate(fromEvent.checkOut);
    }
  }, [fromEvent?.checkIn, fromEvent?.checkOut]);

  useEffect(() => {
    onSelectionChange?.(selections);
  }, [selections, onSelectionChange]);

  useEffect(() => {
    if (!onPayloadChange) return;
    onPayloadChange(buildExternalPayload(selections, checkInDate, checkOutDate));
  }, [selections, checkInDate, checkOutDate, onPayloadChange]);

  const isSearchDisabled = useMemo(() => {
    if (!checkInDate || !checkOutDate) return true;
    const inD = dayjs(checkInDate);
    const outD = dayjs(checkOutDate);
    return !inD.isValid() || !outD.isValid() || !outD.isAfter(inD);
  }, [checkInDate, checkOutDate]);

  const stayNights = useMemo(() => nightsBetween(checkInDate, checkOutDate), [checkInDate, checkOutDate]);

  const groups = useMemo(() => buildGroups(fetchedRooms), [fetchedRooms]);

  const fetchRooms = async () => {
    try {
      setLoading(true);
      setMessage({ display: false, severity: null, message: '' });

      const { data } = await axiosInstance.get(URLS.ycsRooms, {
        params: { check_in_date: checkInDate, check_out_date: checkOutDate },
      });
      const rows: YcsRoomSummary[] = Array.isArray(data) ? data : [];
      setFetchedRooms(rows);
      setDraftByGroup({});
      // Auto-expand only the first group to keep results scannable.
      const nextGroups = buildGroups(rows);
      setExpandedGroups(nextGroups.reduce((acc, g, idx) => ({ ...acc, [g.key]: idx === 0 }), {}));
    } catch (e: any) {
      setFetchedRooms([]);
      setMessage({
        display: true,
        severity: 'error',
        message: e?.response?.data?.message || e?.message || strings.something_went_wrong,
      });
    } finally {
      setLoading(false);
    }
  };

  const updateDraft = (g: RoomGroup, patch: Partial<DraftState>) => {
    // Check if trying to switch to a different rate plan when one is already selected
    if (patch.offerIndex !== undefined && patch.offerIndex !== draftByGroup[g.key]?.offerIndex) {
      if (isAnyRatePlanSelected(g) && !isRoomSelected(g, patch.offerIndex)) {
        setMessage({
          display: true,
          severity: 'warning',
          message: 'A rate plan is already selected. Remove it first before adding a different one.',
        });
        return;
      }
    }

    const base =
      draftByGroup[g.key] || defaultDraftForOffer(g.offers[0], draftByGroup[g.key]?.offerIndex ?? 0);
    let updated: DraftState = { ...base, ...patch };
    if (patch.offerIndex !== undefined) {
      const offer = g.offers[patch.offerIndex];
      if (offer) {
        updated = {
          ...updated,
          offerIndex: patch.offerIndex,
          pricePerNight: num(offer.pricing?.pricePerNight),
        };
      }
    }

    setDraftByGroup(prev => ({ ...prev, [g.key]: updated }));

    const existingSelection = selections.find(s => s.lineId.startsWith(`${g.key}|${updated.offerIndex}`));
    if (existingSelection) {
      setSelections(prev =>
        prev.map(s =>
          s.lineId === existingSelection.lineId
            ? {
                ...s,
                quantity: updated.quantity,
                adultsPerRoom: updated.adultsPerRoom,
                childrenPerRoom: updated.childrenPerRoom,
                pricePerNight: updated.pricePerNight,
              }
            : s,
        ),
      );
    }
  };

  // Check if a room is already selected based on group key and offer index
  const isRoomSelected = (g: RoomGroup, offerIndex: number): boolean => {
    return selections.some(s => s.lineId.startsWith(`${g.key}|${offerIndex}`));
  };

  // Check if any rate plan is already selected for the room group
  const isAnyRatePlanSelected = (g: RoomGroup): boolean => {
    return selections.some(s => s.lineId.startsWith(`${g.key}|`));
  };

  // Get the existing selection for a room
  const getExistingSelection = (g: RoomGroup, offerIndex: number): YcsSelectedRoomLine | undefined => {
    return selections.find(s => s.lineId.startsWith(`${g.key}|${offerIndex}`));
  };

  const addSelection = (g: RoomGroup) => {
    const draft = draftByGroup[g.key] || defaultDraftForOffer(g.offers[0]);
    const offer = g.offers[draft.offerIndex];
    if (!offer) return;

    const maxA = offer.occupancy?.maxAdults;
    const maxC = offer.occupancy?.maxChildren;
    if (maxA != null && draft.adultsPerRoom > maxA) {
      setMessage({
        display: true,
        severity: 'warning',
        message: `Adults per room cannot exceed maximum (${maxA}) for this rate.`,
      });
      return;
    }
    if (maxC != null && draft.childrenPerRoom > maxC) {
      setMessage({
        display: true,
        severity: 'warning',
        message: `Children per room cannot exceed maximum (${maxC}) for this rate.`,
      });
      return;
    }

    const avail = offer.availability?.availableRooms;
    if (avail != null && draft.quantity > avail) {
      setMessage({
        display: true,
        severity: 'warning',
        message: `Only ${avail} room(s) available for this rate.`,
      });
      return;
    }

    const minN = offer.availability?.minNights;
    if (minN != null && stayNights > 0 && stayNights < minN) {
      setMessage({
        display: true,
        severity: 'warning',
        message: `This rate requires at least ${minN} night(s); your stay is ${stayNights} night(s).`,
      });
      return;
    }

    // Check if this room is already selected
    const existingSelection = getExistingSelection(g, draft.offerIndex);

    if (existingSelection) {
      // Update existing selection instead of adding new one
      setSelections(prev =>
        prev.map(s =>
          s.lineId === existingSelection.lineId
            ? {
                ...s,
                quantity: draft.quantity,
                adultsPerRoom: draft.adultsPerRoom,
                childrenPerRoom: draft.childrenPerRoom,
                pricePerNight: draft.pricePerNight,
              }
            : s,
        ),
      );
    } else {
      // Add new selection
      const lineId = `${g.key}|${draft.offerIndex}|${offer.packageName || 'plan'}`;
      const line: YcsSelectedRoomLine = {
        lineId,
        roomId: offer.roomId,
        roomName: offer.roomtypeName || offer.roomName || g.displayName,
        packageName: offer.packageName || offer.roomName,
        quantity: draft.quantity,
        adultsPerRoom: draft.adultsPerRoom,
        childrenPerRoom: draft.childrenPerRoom,
        pricePerNight: draft.pricePerNight,
        summary: offer,
      };
      setSelections(prev => [...prev, line]);
    }
  };

  const removeSelection = (lineId: string) => {
    setSelections(prev => prev.filter(l => l.lineId !== lineId));
  };

  /** Tax-exclusive line subtotal: nights x qty x (base + extras). */
  const estimateLineSubtotal = (line: YcsSelectedRoomLine): number => {
    const base = linePricePerNight(line);
    const extra = perRoomNightExtra(line.summary, line.adultsPerRoom, line.childrenPerRoom);
    return stayNights * line.quantity * (base + extra);
  };

  /** Tax for the whole line: nights x qty x per-room-night tax. */
  const estimateLineTax = (line: YcsSelectedRoomLine): number => {
    const base = linePricePerNight(line);
    const tax = perRoomNightTax(line.summary, base, line.adultsPerRoom, line.childrenPerRoom);
    return stayNights * line.quantity * tax;
  };

  /** Tax-inclusive line total (subtotal + tax). */
  const estimateLineTotal = (line: YcsSelectedRoomLine): number =>
    estimateLineSubtotal(line) + estimateLineTax(line);

  const grandSubtotal = useMemo(
    () => selections.reduce((s, l) => s + estimateLineSubtotal(l), 0),
    [selections, stayNights],
  );
  const grandTax = useMemo(() => selections.reduce((s, l) => s + estimateLineTax(l), 0), [selections, stayNights]);
  const grandTotal = useMemo(() => grandSubtotal + grandTax, [grandSubtotal, grandTax]);

  const displayCurrency = selections[0]?.summary.pricing?.currency || fetchedRooms[0]?.pricing?.currency || '';

  const searchedOnce = fetchedRooms.length > 0;
  const showEmptyInitial = !searchedOnce && !loading && Object.keys(draftByGroup).length === 0 && !message.display;
  const showEmptyNoResults = !searchedOnce && !loading && !showEmptyInitial;

  return (
    <>
      {loading && <PageLoader />}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity}
          closeNotification={(val: boolean) => setMessage(prev => ({ ...prev, display: val }))}
          autoHideDuration={5000}
        />
      )}

      {/* =========================== Existing reservations =========================== */}
      {existingBookedRooms && existingBookedRooms.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: { xs: 2, md: 3 },
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
            background: alpha(theme.palette.info.main, 0.03),
          }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Avatar
                variant="rounded"
                sx={{
                  bgcolor: alpha(theme.palette.info.main, 0.12),
                  color: 'info.main',
                  width: 40,
                  height: 40,
                }}>
                <BookmarkBorderIcon />
              </Avatar>
              <Box>
                <Typography sx={{ fontSize: 16, fontWeight: 700 }}>Existing reservations</Typography>
                <Typography variant="caption" color="text.secondary">
                  {existingBookedRooms.length} room{existingBookedRooms.length === 1 ? '' : 's'} already booked against
                  this line item.
                </Typography>
              </Box>
            </Stack>
          </Stack>

          <Stack spacing={1.5}>
            {existingReservationGroups.map(group => {
              const groupNights =
                group.rooms[0]?.from && group.rooms[0]?.to
                  ? Math.max(
                      0,
                      dayjs(group.rooms[0].to, 'DD-MM-YYYY').diff(dayjs(group.rooms[0].from, 'DD-MM-YYYY'), 'day'),
                    )
                  : group.rooms[0]?.lineItems?.length || 0;
              const groupTotals = computeExistingGroupTotals(group.rooms);
              const subReservationLabels = group.rooms
                .map(room => room.externalSubReservationNos)
                .filter(Boolean)
                .flatMap(value => String(value).split(',').map(s => s.trim()).filter(Boolean));

              return (
                <Paper
                  key={group.reservationKey}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    borderColor: alpha(theme.palette.info.main, 0.35),
                  }}>
                  <Grid container spacing={1.25} alignItems="center" sx={{ mb: group.rooms.length > 1 ? 1 : 0 }}>
                    <Grid item xs={12} md={group.isSharedYcsReservation ? 9 : 5}>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
                        {group.isSharedYcsReservation && group.externalReservationNo ? (
                          <Chip
                            size="small"
                            color="info"
                            variant="outlined"
                            icon={<ConfirmationNumberOutlinedIcon sx={{ fontSize: 14 }} />}
                            label={`${group.externalProvider}: ${group.externalReservationNo}`}
                          />
                        ) : null}
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<BedOutlinedIcon sx={{ fontSize: 14 }} />}
                          label={`${group.rooms.length} room line${group.rooms.length === 1 ? '' : 's'}`}
                        />
                        {group.rooms[0]?.from && group.rooms[0]?.to ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            icon={<EventAvailableOutlinedIcon sx={{ fontSize: 14 }} />}
                            label={`${group.rooms[0].from} → ${group.rooms[0].to}`}
                          />
                        ) : null}
                        {groupNights > 0 ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`${groupNights} night${groupNights === 1 ? '' : 's'}`}
                          />
                        ) : null}
                        {subReservationLabels.map(subNo => (
                          <Chip key={subNo} size="small" variant="outlined" label={subNo} />
                        ))}
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={group.isSharedYcsReservation ? 3 : 7}>
                      <Stack
                        direction="row"
                        justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                        alignItems="center"
                        spacing={1.25}>
                        {groupTotals.total > 0 && (
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1 }}>
                              Total
                            </Typography>
                            {groupTotals.tax > 0 ? (
                              <>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, lineHeight: 1.3 }}>
                                  {formatMoney(displayCurrency, groupTotals.subtotal)} +{' '}
                                  {formatMoney(displayCurrency, groupTotals.tax)}
                                  {groupTotals.baseTaxPercent
                                    ? ` (+${groupTotals.baseTaxPercent}% on room)`
                                    : ''}
                                </Typography>
                                <Typography sx={{ fontWeight: 800, color: 'primary.main', lineHeight: 1.15 }}>
                                  {formatMoney(displayCurrency, groupTotals.total)}{' '}
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: 10.5 }}>
                                    incl. taxes
                                  </Typography>
                                </Typography>
                              </>
                            ) : (
                              <Typography sx={{ fontWeight: 800, color: 'primary.main', lineHeight: 1.1 }}>
                                {formatMoney(displayCurrency, groupTotals.total)}
                              </Typography>
                            )}
                          </Box>
                        )}
                        {onCancelExistingBookedRoom && (
                          <Tooltip
                            title={
                              group.isSharedYcsReservation
                                ? `Cancel entire reservation ${group.externalReservationNo} on ${group.externalProvider}`
                                : 'Remove this room from the booking'
                            }>
                            <span>
                              <Button
                                size="small"
                                color="error"
                                variant="outlined"
                                startIcon={<EventBusyOutlinedIcon />}
                                onClick={() => setCancelCandidate(group)}
                                sx={{ whiteSpace: 'nowrap' }}>
                                {group.isSharedYcsReservation ? 'Cancel reservation' : 'Remove room'}
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    </Grid>
                  </Grid>

                  {group.rooms.length > 1 && (
                    <Stack spacing={1}>
                      {group.rooms.map(room => {
                        const qty = room.numberOfRooms || room.lineItems?.[0]?.quantity || 1;
                        const roomTotals = computeExistingRoomTotals(room);
                        return (
                          <Box
                            key={room.bookedRoomId}
                            sx={{
                              pl: 1.5,
                              py: 1,
                              borderLeft: `3px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                            }}>
                            <Grid container spacing={1} alignItems="center">
                              <Grid item xs={12} sm={6}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <KingBedOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                      {room.type || room.roomTypeCode || 'Room'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                                      {room.packageName || room.mealPlanName || 'Standard rate'}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <Typography variant="caption" color="text.secondary">
                                  {qty} room{qty === 1 ? '' : 's'}
                                  {(room.adultsPerRoom || 0) + (room.childrenPerRoom || 0) > 0
                                    ? ` · ${room.adultsPerRoom || 0} adult${
                                        (room.adultsPerRoom || 0) === 1 ? '' : 's'
                                      }${
                                        (room.childrenPerRoom || 0) > 0
                                          ? ` · ${room.childrenPerRoom} child${
                                              (room.childrenPerRoom || 0) === 1 ? '' : 'ren'
                                            }`
                                          : ''
                                      }`
                                    : ''}
                                </Typography>
                              </Grid>
                              {roomTotals.total > 0 && (
                                <Grid item xs={12} sm={2} sx={{ textAlign: { sm: 'right' } }}>
                                  {roomTotals.tax > 0 ? (
                                    <>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5, lineHeight: 1.2 }}>
                                        {formatMoney(displayCurrency, roomTotals.subtotal)} +{' '}
                                        {formatMoney(displayCurrency, roomTotals.tax)}
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1.2 }}>
                                        {formatMoney(displayCurrency, roomTotals.total)}
                                      </Typography>
                                    </>
                                  ) : (
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                      {formatMoney(displayCurrency, roomTotals.total)}
                                    </Typography>
                                  )}
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        );
                      })}
                    </Stack>
                  )}

                  {group.rooms.length === 1 && (
                    <Box sx={{ pt: 0.5 }}>
                      {(() => {
                        const room = group.rooms[0];
                        const qty = room.numberOfRooms || room.lineItems?.[0]?.quantity || 1;
                        return (
                          <Typography variant="caption" color="text.secondary">
                            {room.type || room.roomTypeCode || 'Room'}
                            {room.packageName || room.mealPlanName ? ` · ${room.packageName || room.mealPlanName}` : ''}
                            {' · '}
                            {qty} room{qty === 1 ? '' : 's'}
                          </Typography>
                        );
                      })()}
                    </Box>
                  )}
                </Paper>
              );
            })}
          </Stack>
        </Paper>
      )}

      {/* Confirm-cancel dialog for an existing reservation. */}
      <Dialog
        open={!!cancelCandidate}
        onClose={() => setCancelCandidate(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
          },
        }}>
        {cancelCandidate && (() => {
          const firstRoom = cancelCandidate.rooms[0];
          const modalNights =
            firstRoom?.from && firstRoom?.to
              ? Math.max(0, dayjs(firstRoom.to, 'DD-MM-YYYY').diff(dayjs(firstRoom.from, 'DD-MM-YYYY'), 'day'))
              : firstRoom?.lineItems?.length || 0;
          const modalTotals = computeExistingGroupTotals(cancelCandidate.rooms);
          const isShared = cancelCandidate.isSharedYcsReservation;

          return (
            <>
              <Box
                sx={{
                  px: 3,
                  pt: 3,
                  pb: 2.5,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(
                    theme.palette.error.main,
                    0,
                  )} 72%)`,
                  borderBottom: `1px solid ${alpha(theme.palette.error.main, 0.12)}`,
                }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Avatar
                    variant="rounded"
                    sx={{
                      bgcolor: alpha(theme.palette.error.main, 0.14),
                      color: 'error.main',
                      width: 48,
                      height: 48,
                    }}>
                    <EventBusyOutlinedIcon />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.25 }}>
                      {isShared ? 'Cancel reservation?' : 'Remove room?'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {isShared
                        ? `This will cancel the live ${cancelCandidate.externalProvider || 'channel manager'} reservation and remove all linked rooms from this booking.`
                        : 'This will remove the room from this booking only.'}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: 2,
                    borderColor: alpha(theme.palette.info.main, 0.35),
                    bgcolor: alpha(theme.palette.info.main, 0.03),
                  }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.3 }}>
                    RESERVATION SUMMARY
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.25, mb: 1.5 }}>
                    {isShared && cancelCandidate.externalReservationNo ? (
                      <Chip
                        size="small"
                        color="info"
                        variant="outlined"
                        icon={<ConfirmationNumberOutlinedIcon sx={{ fontSize: 14 }} />}
                        label={`${cancelCandidate.externalProvider}: ${cancelCandidate.externalReservationNo}`}
                      />
                    ) : null}
                    <Chip
                      size="small"
                      variant="outlined"
                      icon={<BedOutlinedIcon sx={{ fontSize: 14 }} />}
                      label={`${cancelCandidate.rooms.length} room line${cancelCandidate.rooms.length === 1 ? '' : 's'}`}
                    />
                    {firstRoom?.from && firstRoom?.to ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<EventAvailableOutlinedIcon sx={{ fontSize: 14 }} />}
                        label={`${firstRoom.from} → ${firstRoom.to}`}
                      />
                    ) : null}
                    {modalNights > 0 ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<NightsStayOutlinedIcon sx={{ fontSize: 14 }} />}
                        label={`${modalNights} night${modalNights === 1 ? '' : 's'}`}
                      />
                    ) : null}
                  </Stack>

                  {cancelCandidate.rooms.length === 1 ? (
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {firstRoom?.type || firstRoom?.roomTypeCode || 'Room'}
                      {firstRoom?.packageName || firstRoom?.mealPlanName
                        ? ` · ${firstRoom.packageName || firstRoom.mealPlanName}`
                        : ''}
                    </Typography>
                  ) : (
                    <Stack spacing={0.75}>
                      {cancelCandidate.rooms.map(room => (
                        <Typography key={room.bookedRoomId} variant="body2" sx={{ fontWeight: 600 }}>
                          {room.type || room.roomTypeCode || 'Room'}
                          {room.packageName || room.mealPlanName
                            ? ` · ${room.packageName || room.mealPlanName}`
                            : ''}
                        </Typography>
                      ))}
                    </Stack>
                  )}

                  {modalTotals.total > 0 && (
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px dashed ${alpha(theme.palette.divider, 0.8)}` }}>
                      {modalTotals.tax > 0 ? (
                        <>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, lineHeight: 1.3 }}>
                            {formatMoney(displayCurrency, modalTotals.subtotal)} +{' '}
                            {formatMoney(displayCurrency, modalTotals.tax)}
                            {modalTotals.baseTaxPercent ? ` (+${modalTotals.baseTaxPercent}% on room)` : ''}
                          </Typography>
                          <Typography sx={{ fontWeight: 800, color: 'primary.main', lineHeight: 1.2, mt: 0.25 }}>
                            {formatMoney(displayCurrency, modalTotals.total)}{' '}
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ fontSize: 10.5 }}>
                              incl. taxes
                            </Typography>
                          </Typography>
                        </>
                      ) : (
                        <Typography sx={{ fontWeight: 800, color: 'primary.main' }}>
                          {formatMoney(displayCurrency, modalTotals.total)}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Paper>

                <Alert
                  severity="error"
                  variant="outlined"
                  icon={<WarningAmberOutlinedIcon fontSize="inherit" />}
                  sx={{ borderRadius: 2, alignItems: 'flex-start' }}>
                  {isShared ? (
                    <>
                      Reservation <strong>{cancelCandidate.externalReservationNo}</strong> will be cancelled on{' '}
                      <strong>{cancelCandidate.externalProvider}</strong> and{' '}
                      <strong>
                        {cancelCandidate.rooms.length} room{cancelCandidate.rooms.length === 1 ? '' : 's'}
                      </strong>{' '}
                      will be removed from this booking. This cannot be undone.
                    </>
                  ) : (
                    <>The room will be removed from this booking. This cannot be undone.</>
                  )}
                </Alert>
              </DialogContent>

              <DialogActions sx={{ px: 3, py: 2.5, pt: 1, gap: 1 }}>
                <Button onClick={() => setCancelCandidate(null)} variant="outlined" color="inherit" sx={{ minWidth: 120 }}>
                  Keep
                </Button>
                <Button
                  color="error"
                  variant="contained"
                  startIcon={<EventBusyOutlinedIcon />}
                  onClick={() => {
                    if (onCancelExistingBookedRoom) {
                      onCancelExistingBookedRoom(cancelCandidate);
                    }
                    setCancelCandidate(null);
                  }}
                  sx={{ minWidth: 160, fontWeight: 600 }}>
                  {isShared ? 'Cancel reservation' : 'Remove room'}
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* ============================== Search card ============================== */}
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(
            theme.palette.primary.main,
            0,
          )} 65%)`,
        }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={1}
          sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              variant="rounded"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: 'primary.main',
                width: 40,
                height: 40,
              }}>
              <SearchRoundedIcon />
            </Avatar>
            <Box>
              <Typography sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.1 }}>Search availability</Typography>
              <Typography variant="caption" color="text.secondary">
                Pick your dates to see live YCS room types and rate plans.
              </Typography>
            </Box>
          </Stack>

          {fromEvent ? (
            <Chip
              size="small"
              icon={<EventAvailableOutlinedIcon sx={{ fontSize: 16 }} />}
              label="Dates aligned with event"
              color="primary"
              variant="outlined"
            />
          ) : null}
        </Stack>

        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} sm={6} md={3}>
            {/* Use the shared DateInput so the picker matches the rest of the app. */}
            <Box sx={{ '& .MuiFormControl-root, & .MuiTextField-root': { width: '100%' } }}>
              <DateInput
                field="checkInDate"
                label="Check-in"
                inputFormat={identifiers.date_format}
                value={checkInDate ? dayjs(checkInDate) : null}
                textChange={(val: string) => {
                  if (!val) return;
                  setCheckInDate(val);
                  if (checkOutDate && !dayjs(checkOutDate).isAfter(dayjs(val))) {
                    setCheckOutDate(dayjs(val).add(1, 'day').format('YYYY-MM-DD'));
                  }
                }}
                onChange={() => {
                  /* handled via textChange above; onChange satisfies the picker's required prop */
                }}
                renderInput={() => <></>}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ '& .MuiFormControl-root, & .MuiTextField-root': { width: '100%' } }}>
              <DateInput
                field="checkOutDate"
                label="Check-out"
                inputFormat={identifiers.date_format}
                value={checkOutDate ? dayjs(checkOutDate) : null}
                minDate={checkInDate ? dayjs(checkInDate).add(1, 'day') : undefined}
                textChange={(val: string) => {
                  if (!val) return;
                  setCheckOutDate(val);
                }}
                onChange={() => {
                  /* handled via textChange above; onChange satisfies the picker's required prop */
                }}
                renderInput={() => <></>}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                px: 2,
                py: 1.25,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                background: 'background.paper',
              }}>
              <NightsStayOutlinedIcon color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1 }}>
                  Stay length
                </Typography>
                <Typography sx={{ fontWeight: 600 }}>
                  {stayNights} night{stayNights === 1 ? '' : 's'}
                </Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              size="large"
              variant="contained"
              disabled={isSearchDisabled}
              onClick={fetchRooms}
              startIcon={<SearchRoundedIcon />}
              sx={{
                height: '100%',
                minHeight: 48,
                fontWeight: 600,
                letterSpacing: 0.2,
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}>
              Search rooms
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* ============================== Selected rooms ============================== */}
      {selections.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: { xs: 2, md: 3 },
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
            background: alpha(theme.palette.success.main, 0.03),
          }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Avatar
                variant="rounded"
                sx={{
                  bgcolor: alpha(theme.palette.success.main, 0.12),
                  color: 'success.main',
                  width: 40,
                  height: 40,
                }}>
                <AddShoppingCartIcon />
              </Avatar>
              <Box>
                <Typography sx={{ fontSize: 16, fontWeight: 700 }}>Selected rooms</Typography>
                <Typography variant="caption" color="text.secondary">
                  {selections.length} line{selections.length === 1 ? '' : 's'} · {stayNights} night
                  {stayNights === 1 ? '' : 's'}
                </Typography>
              </Box>
            </Stack>

            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                px: 2,
                py: 1,
                minWidth: 200,
                textAlign: 'right',
                borderColor: alpha(theme.palette.success.main, 0.35),
              }}>
              <Stack direction="row" justifyContent="space-between" spacing={2}>
                <Typography variant="caption" color="text.secondary">
                  Subtotal (excl. tax)
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {formatMoney(displayCurrency, grandSubtotal)}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" spacing={2}>
                <Typography variant="caption" color="text.secondary">
                  Taxes
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {formatMoney(displayCurrency, grandTax)}
                </Typography>
              </Stack>
              <Divider sx={{ my: 0.5 }} />
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Estimated total
                </Typography>
                <Typography sx={{ fontWeight: 800, fontSize: 20, color: 'success.main', lineHeight: 1.1 }}>
                  {formatMoney(displayCurrency, grandTotal)}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5 }}>
                per-night rate + extras + taxes
              </Typography>
            </Paper>
          </Stack>

          <Table size="small" sx={{ '& th': { fontWeight: 700, color: 'text.secondary' } }}>
            <TableHead>
              <TableRow>
                <TableCell>Room</TableCell>
                <TableCell>Rate plan</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Adults</TableCell>
                <TableCell align="right">Children</TableCell>
                <TableCell align="right">Price / night (excl. tax)</TableCell>
                <TableCell align="right">Tax</TableCell>
                <TableCell align="right">Line total</TableCell>
                <TableCell align="right" width={56} />
              </TableRow>
            </TableHead>
            <TableBody>
              {selections.map(line => (
                <TableRow key={line.lineId} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Avatar
                        variant="rounded"
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: 'primary.main',
                          width: 28,
                          height: 28,
                        }}>
                        <BedOutlinedIcon sx={{ fontSize: 18 }} />
                      </Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {line.roomName || '-'}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{line.packageName || '-'}</Typography>
                  </TableCell>
                  <TableCell align="right">{line.quantity}</TableCell>
                  <TableCell align="right">{line.adultsPerRoom}</TableCell>
                  <TableCell align="right">{line.childrenPerRoom}</TableCell>
                  <TableCell align="right">{formatMoney(line.summary.pricing?.currency, linePricePerNight(line))}</TableCell>
                  <TableCell align="right">{formatMoney(line.summary.pricing?.currency, estimateLineTax(line))}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {formatMoney(line.summary.pricing?.currency, estimateLineTotal(line))}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Remove">
                      <IconButton size="small" aria-label="Remove" onClick={() => removeSelection(line.lineId)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* ============================== Room groups ============================== */}
      {groups.length > 0 && (
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, mt: 0.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
            {groups.length} room type{groups.length === 1 ? '' : 's'} available
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Sorted by lowest rate plan
          </Typography>
        </Stack>
      )}

      <Stack spacing={1.25}>
        {groups.map(g => {
          const draft = draftByGroup[g.key] || defaultDraftForOffer(g.offers[0]);
          const selectedOffer = g.offers[draft.offerIndex];
          const maxAdults = selectedOffer?.occupancy?.maxAdults ?? 99;
          const maxChildren = selectedOffer?.occupancy?.maxChildren ?? 99;
          const maxQty = selectedOffer?.availability?.availableRooms ?? 999;
          const ppn = draft.pricePerNight > 0 ? draft.pricePerNight : num(selectedOffer?.pricing?.pricePerNight);
          const extra = selectedOffer
            ? perRoomNightExtra(selectedOffer, draft.adultsPerRoom, draft.childrenPerRoom)
            : 0;
          // Tax re-applies the effective percent to the (possibly overridden) base + extras.
          const tax = selectedOffer
            ? perRoomNightTax(selectedOffer, ppn, draft.adultsPerRoom, draft.childrenPerRoom)
            : 0;
          const perNightPerRoom = ppn + extra + tax;
          const estimateSubtotal = stayNights * draft.quantity * (ppn + extra);
          const estimateTax = stayNights * draft.quantity * tax;
          const estimate = stayNights * draft.quantity * perNightPerRoom;
          const cur = selectedOffer?.pricing?.currency;
          const taxPct = num(selectedOffer?.pricing?.taxPercent);
          const soldOut = (selectedOffer?.availability?.availableRooms ?? 0) <= 0;
          const expanded = !!expandedGroups[g.key];
          const toggleExpanded = () => setExpandedGroups(prev => ({ ...prev, [g.key]: !prev[g.key] }));

          return (
            <Card
              key={g.key}
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border: `1px solid ${theme.palette.divider}`,
                transition: 'border-color 120ms ease, box-shadow 120ms ease',
                '&:hover': {
                  borderColor: alpha(theme.palette.primary.main, 0.35),
                },
              }}>
              {/* Group header (click to expand/collapse) */}
              <Box
                onClick={toggleExpanded}
                sx={{
                  px: { xs: 1.5, md: 2 },
                  py: 1.25,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  cursor: 'pointer',
                  userSelect: 'none',
                  borderBottom: expanded ? `1px solid ${theme.palette.divider}` : 'none',
                  '&:hover': { background: alpha(theme.palette.primary.main, 0.02) },
                }}>
                <Avatar
                  variant="rounded"
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    width: 34,
                    height: 34,
                  }}>
                  <KingBedOutlinedIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.15 }} noWrap>
                    {g.displayName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap component="div">
                    {expanded
                      ? `${g.offers.length} rate plan${g.offers.length === 1 ? '' : 's'}`
                      : `${selectedOffer?.packageName || selectedOffer?.roomName || '—'} · ${draft.quantity} room${
                          draft.quantity === 1 ? '' : 's'
                        } × ${stayNights} night${stayNights === 1 ? '' : 's'}`}
                  </Typography>
                </Box>

                {ppn > 0 && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {isAnyRatePlanSelected(g) && <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />}
                    <Box sx={{ textAlign: 'right', mr: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1 }}>
                        {expanded ? 'From' : 'Est.'}
                      </Typography>
                      <Typography sx={{ fontWeight: 800, fontSize: 16, color: 'primary.main', lineHeight: 1.1 }}>
                        {formatMoney(cur, expanded ? ppn : estimate)}
                      </Typography>
                    </Box>
                  </Stack>
                )}

                <IconButton
                  size="small"
                  aria-label={expanded ? 'Collapse' : 'Expand'}
                  onClick={e => {
                    e.stopPropagation();
                    toggleExpanded();
                  }}
                  sx={{
                    transition: 'transform 180ms ease',
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}>
                  <ExpandMoreIcon />
                </IconButton>
              </Box>

              <Collapse in={expanded} timeout={180} unmountOnExit>
                <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>
                  {/* Rate plan tiles */}
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.6 }}>
                    Rate plan
                  </Typography>
                  <Grid container spacing={1} sx={{ mb: 1.25, mt: 0.25 }}>
                    {g.offers.map((offer, idx) => {
                      const oc = offer.pricing?.currency;
                      const op = offer.pricing?.pricePerNight;
                      const od = offer.pricing?.discountPercent;
                      const otp = num(offer.pricing?.taxPercent);
                      const oa = offer.availability?.availableRooms;
                      const os = offer.availability?.stopSell;
                      const oMin = offer.availability?.minNights;
                      const selected = idx === draft.offerIndex;
                      const offerSoldOut = (oa ?? 0) <= 0;

                      return (
                        <Grid item xs={12} md={6} key={`${g.key}-offer-${idx}`}>
                          <Paper
                            role="button"
                            tabIndex={0}
                            onClick={() => updateDraft(g, { offerIndex: idx })}
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                updateDraft(g, { offerIndex: idx });
                              }
                            }}
                            elevation={0}
                            sx={{
                              cursor: 'pointer',
                              borderRadius: 1.5,
                              p: 1.25,
                              outline: 'none',
                              position: 'relative',
                              border: `1.5px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
                              background: selected ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
                              transition: 'all 120ms ease',
                              '&:hover': {
                                borderColor: selected
                                  ? theme.palette.primary.main
                                  : alpha(theme.palette.primary.main, 0.4),
                                background: selected
                                  ? alpha(theme.palette.primary.main, 0.06)
                                  : alpha(theme.palette.primary.main, 0.02),
                              },
                              '&:focus-visible': {
                                boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.25)}`,
                              },
                            }}>
                            {selected ? (
                              <CheckCircleIcon
                                color="primary"
                                sx={{ position: 'absolute', top: 8, right: 8, fontSize: 18 }}
                              />
                            ) : (
                              <AddCircleOutlineIcon
                                sx={{ position: 'absolute', top: 8, right: 8, fontSize: 18, color: 'text.secondary' }}
                              />
                            )}
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5, pr: 3 }}>
                              <LocalOfferIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                                {offer.packageName || offer.roomName || '—'}
                              </Typography>
                            </Stack>

                            {op != null && (
                              <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ mb: 0.75 }}>
                                <Typography
                                  sx={{ fontWeight: 800, fontSize: 17, color: 'primary.main', lineHeight: 1.15 }}>
                                  {formatMoney(oc, num(op))}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  / night excl. tax {otp > 0 ? `· +${otp}% tax` : ''}
                                </Typography>
                              </Stack>
                            )}

                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {od != null && od > 0 ? (
                                <Chip
                                  size="small"
                                  label={`${od}% off`}
                                  color="success"
                                  variant="outlined"
                                  icon={<LocalOfferIcon sx={{ fontSize: 14 }} />}
                                />
                              ) : null}
                              {oa != null ? (
                                <Chip
                                  size="small"
                                  color={offerSoldOut ? 'default' : 'success'}
                                  variant="outlined"
                                  label={offerSoldOut ? 'Sold out' : `Available: ${oa}`}
                                />
                              ) : null}
                              {oMin != null ? (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={`Min ${oMin} night${oMin === 1 ? '' : 's'}`}
                                />
                              ) : null}
                              {os ? <Chip size="small" label="Stop sell" color="warning" /> : null}
                            </Stack>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>

                  <Divider sx={{ my: 1.25 }} />

                  {/* Booking controls */}
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} md={8}>
                      <Typography
                        variant="overline"
                        color="text.secondary"
                        sx={{ fontWeight: 700, letterSpacing: 0.6 }}>
                        Guests &amp; rooms
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={0.75}
                        alignItems="flex-start"
                        sx={{ mt: 0.5, width: '100%', flexWrap: 'nowrap' }}>
                        <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                          <Stepper
                            label="Rooms"
                            value={draft.quantity}
                            min={1}
                            max={maxQty}
                            onChange={n => updateDraft(g, { quantity: n })}
                          />
                        </Box>
                        <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                          <Stepper
                            label="Adults"
                            value={draft.adultsPerRoom}
                            min={1}
                            max={maxAdults}
                            onChange={n => updateDraft(g, { adultsPerRoom: n })}
                          />
                        </Box>
                        <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                          <Stepper
                            label="Children"
                            value={draft.childrenPerRoom}
                            min={0}
                            max={maxChildren}
                            onChange={n => updateDraft(g, { childrenPerRoom: n })}
                          />
                        </Box>
                        <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                          <TextField
                            size="small"
                            label="Price"
                            type="text"
                            inputMode="decimal"
                            value={draft.pricePerNight}
                            onChange={e => {
                              const parsed = parsePriceInput(e.target.value);
                              if (parsed === null) return;
                              updateDraft(g, { pricePerNight: parsed });
                            }}
                            inputProps={{
                              style: { textAlign: 'center' },
                              onWheel: blockScrollWheelOnInput,
                            }}
                            sx={compactPriceFieldSx}
                          />
                        </Box>
                      </Stack>

                      {selectedOffer && (
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                          sx={{ color: 'text.secondary', mt: 1, whiteSpace: 'nowrap' }}>
                          <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                          <Typography variant="caption" whiteSpace="break-spaces">
                            Base (included): {selectedOffer.occupancy?.baseAdults ?? 0} adults ·{' '}
                            {selectedOffer.occupancy?.baseChildren ?? 0} children · Extra adult{' '}
                            {formatMoney(cur, num(selectedOffer.extraPricing?.extraAdult))} · Extra child{' '}
                            {formatMoney(cur, num(selectedOffer.extraPricing?.extraChild))}
                            {selectedOffer.availability?.minNights != null
                              ? ` · Min ${selectedOffer.availability.minNights} night(s)`
                              : ''}
                          </Typography>
                        </Stack>
                      )}
                    </Grid>

                    <Grid item xs={12} md={4}>
                      {/* Estimate + CTA */}
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1,
                          borderRadius: 1.5,
                          borderColor: alpha(theme.palette.primary.main, 0.25),
                          background: alpha(theme.palette.primary.main, 0.03),
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 1,
                        }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1 }}>
                            Estimated total · {draft.quantity} room{draft.quantity === 1 ? '' : 's'} × {stayNights}{' '}
                            night
                            {stayNights === 1 ? '' : 's'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, lineHeight: 1.3 }}>
                            {formatMoney(cur, estimateSubtotal)} + {formatMoney(cur, estimateTax)}
                            {taxPct > 0 ? ` (+${taxPct}% on room)` : ''}
                          </Typography>
                          <Typography sx={{ fontWeight: 800, fontSize: 18, color: 'primary.main', lineHeight: 1.15 }}>
                            {formatMoney(cur, estimate)}{' '}
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ fontSize: 10.5 }}>
                              incl. taxes
                            </Typography>
                          </Typography>
                        </Box>
                        <Button
                          variant={isRoomSelected(g, draft.offerIndex) ? 'outlined' : 'contained'}
                          color={isRoomSelected(g, draft.offerIndex) ? 'success' : 'primary'}
                          size="medium"
                          disabled={soldOut || stayNights <= 0}
                          startIcon={
                            isRoomSelected(g, draft.offerIndex) ? <CheckCircleIcon /> : <AddShoppingCartIcon />
                          }
                          onClick={() => {
                            if (isRoomSelected(g, draft.offerIndex)) {
                              // Remove selection if already selected
                              const existing = getExistingSelection(g, draft.offerIndex);
                              if (existing) {
                                removeSelection(existing.lineId);
                              }
                            } else {
                              addSelection(g);
                            }
                          }}
                          sx={{
                            fontWeight: 600,
                            letterSpacing: 0.2,
                            boxShadow: 'none',
                            '&:hover': { boxShadow: 'none' },
                            width: '100%',
                          }}>
                          {soldOut ? 'Sold out' : isRoomSelected(g, draft.offerIndex) ? 'Remove' : 'Add to booking'}
                        </Button>
                      </Paper>
                    </Grid>
                  </Grid>
                </CardContent>
              </Collapse>
            </Card>
          );
        })}
      </Stack>

      {/* ============================== Empty state ============================== */}
      {groups.length === 0 && !loading ? (
        <Paper
          elevation={0}
          sx={{
            mt: 1,
            p: { xs: 3, md: 5 },
            borderRadius: 3,
            border: `1px dashed ${theme.palette.divider}`,
            textAlign: 'center',
            background: alpha(theme.palette.primary.main, 0.02),
          }}>
          <Avatar
            variant="rounded"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              width: 52,
              height: 52,
              mx: 'auto',
              mb: 1.5,
            }}>
            <KingBedOutlinedIcon />
          </Avatar>
          <Typography sx={{ fontWeight: 700, fontSize: 16 }}>No rooms loaded yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Pick your check-in and check-out dates above, then hit <strong>Search rooms</strong> to see live YCS
            availability and rate plans.
          </Typography>
        </Paper>
      ) : null}
    </>
  );
};
